import { clampManualMicrophoneGain, DEFAULT_MANUAL_MICROPHONE_GAIN } from "../../Stores/MicrophoneGainConfig";
import { audioContextManager } from "../AudioContextManager";

const MANUAL_GAIN_RAMP_DURATION_SECONDS = 0.05;

/**
 * Applies a user-controlled gain stage to the captured microphone stream.
 *
 * Pipeline per stream:
 *   MediaStreamAudioSourceNode
 *     -> GainNode
 *       -> MediaStreamAudioDestinationNode
 *
 * Video tracks are forwarded unchanged.
 */
export class ManualGainProcessor {
    private audioContext: AudioContext | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private gainNode: GainNode | null = null;
    private destinationNode: MediaStreamAudioDestinationNode | null = null;
    private outputTrack: MediaStreamTrack | null = null;
    private closed = false;
    private currentGain = DEFAULT_MANUAL_MICROPHONE_GAIN;

    async init(): Promise<void> {
        this.closed = false;
        this.audioContext = audioContextManager.getContext();
        await this.audioContext.resume();
    }

    async processStream(input: MediaStream): Promise<MediaStream> {
        if (this.closed) {
            throw new Error("[ManualGainProcessor][ProcessStream][ERROR] Processor already closed");
        }
        if (!this.audioContext) {
            throw new Error("[ManualGainProcessor][ProcessStream][ERROR] Processor not initialized");
        }

        this.disconnectNodes({ stopOutputTrack: true });

        const audioTracks = input.getAudioTracks();
        if (audioTracks.length === 0) {
            return input;
        }

        const audioOnlyStream = new MediaStream(audioTracks);
        this.sourceNode = this.audioContext.createMediaStreamSource(audioOnlyStream);
        this.gainNode = this.audioContext.createGain();
        this.destinationNode = this.audioContext.createMediaStreamDestination();

        this.sourceNode.connect(this.gainNode);
        this.gainNode.connect(this.destinationNode);
        this.applyGain(this.currentGain, { immediate: true });

        const processedAudioTrack = this.destinationNode.stream.getAudioTracks()[0];
        this.outputTrack = processedAudioTrack ?? null;

        const outputStream = new MediaStream();
        if (processedAudioTrack) {
            outputStream.addTrack(processedAudioTrack);
        }
        for (const track of input.getVideoTracks()) {
            outputStream.addTrack(track);
        }

        return outputStream;
    }

    updateGain(value: number): void {
        const nextGain = clampManualMicrophoneGain(value);
        this.currentGain = nextGain;
        this.applyGain(nextGain);
    }

    close(): void {
        this.closed = true;
        this.disconnectNodes({ stopOutputTrack: true });
        this.audioContext = null;
    }

    private applyGain(value: number, options?: { immediate?: boolean }): void {
        if (!this.audioContext || !this.gainNode) {
            return;
        }

        const targetGain = clampManualMicrophoneGain(value);
        const gainParameter = this.gainNode.gain;
        const now = this.audioContext.currentTime;

        gainParameter.cancelScheduledValues(now);

        if (options?.immediate) {
            gainParameter.setValueAtTime(targetGain, now);
            return;
        }

        gainParameter.setValueAtTime(gainParameter.value, now);
        gainParameter.linearRampToValueAtTime(targetGain, now + MANUAL_GAIN_RAMP_DURATION_SECONDS);
    }

    private disconnectNodes(options?: { stopOutputTrack?: boolean }): void {
        try {
            if (this.sourceNode) {
                this.sourceNode.disconnect();
                this.sourceNode = null;
            }
            if (this.gainNode) {
                this.gainNode.disconnect();
                this.gainNode = null;
            }
            if (this.destinationNode) {
                this.destinationNode.disconnect();
                this.destinationNode = null;
            }
            if (options?.stopOutputTrack && this.outputTrack) {
                this.outputTrack.stop();
            }
        } catch (err) {
            console.warn("[ManualGainProcessor][WARN] Error disconnecting audio nodes", err);
        }

        this.outputTrack = null;
    }
}
